import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import * as sfx from '../labs/launchSound.js';
import * as music from '../labs/bootMusic.js';
import { useVoice, speak, hush, canSpeak } from '../../lib/speech.js';

/**
 * The animated-explainer player, extracted from the boot-loader journey
 * so the Software Basics module can have four of them without four
 * copies of the same clock.
 *
 * A scene is a title, a mood for the music bed, a drawing, and a list
 * of beats. A beat is one line of narration, optionally with a sound
 * cue that lands on the thing it is about.
 *
 * The one real improvement over the original: when narration is on, the
 * timeline waits for the voice rather than running a fixed timer
 * underneath it. A four-second beat with a seven-second sentence in it
 * is not a synchronised voiceover, it is two things happening at once.
 * With narration off it falls back to the timer, because then there is
 * nothing to synchronise to.
 *
 * Everything is 16:9, captions are on by default, and the whole thing
 * can be driven from the keyboard.
 */

const BEAT_MS = 4200;      // when nothing is speaking
const AFTER_SPEECH_MS = 550;  // a breath between sentences

const storeKey = (id) => `cybercub.explainer.${id}`;

const load = (id) => {
  try { return JSON.parse(localStorage.getItem(storeKey(id)) ?? '{}'); } catch { return {}; }
};
const save = (id, v) => {
  try { localStorage.setItem(storeKey(id), JSON.stringify(v)); } catch { /* private window */ }
};

export default function Explainer({
  id,
  title,
  subtitle,
  backTo,
  backLabel = '← Back to the lesson',
  scenes,
  viewBox = '0 0 160 90',
  closing,
  Defs,
}) {
  const saved = useMemo(() => load(id), [id]);
  const calm = useMemo(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
    [],
  );

  const [si, setSi] = useState(0);
  const [beat, setBeat] = useState(0);
  const [playing, setPlaying] = useState(!calm);
  const [sound, setSound] = useState(false);
  const [narrating, setNarrating] = useState(false);
  const [subs, setSubs] = useState(saved.subs ?? true);
  const [watched, setWatched] = useState(Boolean(saved.watched));

  const voice = useVoice();
  const scene = scenes[si];

  // Clamped: a scene can be jumped to from a longer one before the beat
  // has been reset, and reading past the end takes the page down.
  const line = scene.beats[Math.min(beat, scene.beats.length - 1)];
  const lastScene = si === scenes.length - 1;
  const atEnd = lastScene && beat >= scene.beats.length - 1;
  const finished = atEnd && !playing;

  const totalBeats = useMemo(
    () => scenes.reduce((n, s) => n + s.beats.length, 0),
    [scenes],
  );

  useEffect(() => { document.title = `${title} · CyberCub`; }, [title]);
  useEffect(() => { save(id, { watched, subs }); }, [id, watched, subs]);

  /** Move on: next beat, next scene, or stop at the end. */
  const advance = useRef(() => {});
  advance.current = () => {
    if (beat + 1 < scene.beats.length) { setBeat((b) => b + 1); return; }
    if (!lastScene) { setSi((n) => n + 1); setBeat(0); return; }
    setPlaying(false);
    setWatched(true);
  };

  /* The clock, for when nothing is speaking. */
  useEffect(() => {
    if (!playing || narrating) return undefined;
    const t = setTimeout(() => advance.current(), BEAT_MS);
    return () => clearTimeout(t);
  }, [playing, narrating, beat, si]);

  /* The narrator. While it is on it is also the clock: the next beat
     starts when this sentence has actually finished. */
  useEffect(() => {
    if (!narrating) return undefined;

    let gone = false;
    let timer;

    speak(line.say, voice, {
      onStart: () => music.duck(true),
      onEnd: () => {
        music.duck(false);
        if (gone || !playing) return;
        timer = setTimeout(() => advance.current(), AFTER_SPEECH_MS);
      },
    });

    return () => {
      gone = true;
      clearTimeout(timer);
      hush();
      music.duck(false);
    };
  }, [si, beat, narrating, playing, voice, line.say]);

  /* Reaching the last line counts, however you got there. The original
     only marked it watched when the clock ran out on the final beat, so
     anybody who stepped through with the Next button never saw the
     closing card. */
  useEffect(() => { if (atEnd) setWatched(true); }, [atEnd]);

  /* The cue belonging to this beat. */
  useEffect(() => {
    if (sound && line.cue) sfx.play(line.cue);
  }, [si, beat, sound, line.cue]);

  /* The bed follows the scene's mood, and glides rather than cuts. */
  useEffect(() => {
    if (sound) music.setMood(scene.mood);
  }, [sound, scene.mood]);

  /* Never leave a voice talking to an empty room. */
  useEffect(() => () => { hush(); music.stop(); }, []);

  const goScene = (n) => { setSi(n); setBeat(0); };

  const step = (d) => {
    setPlaying(false);
    const next = beat + d;
    if (next >= 0 && next < scene.beats.length) { setBeat(next); return; }
    if (d > 0 && !lastScene) { setSi(si + 1); setBeat(0); return; }
    if (d < 0 && si > 0) { setSi(si - 1); setBeat(scenes[si - 1].beats.length - 1); }
  };

  const toggleSound = () => setSound((on) => {
    if (on) { music.stop(); sfx.sleep(); return false; }
    const ok = sfx.wake();          // must happen inside the click
    if (ok) {
      music.start(scene.mood);
      if (line.cue) sfx.play(line.cue);
    }
    return ok;
  });

  const toggleNarration = () => setNarrating((on) => {
    if (on) { hush(); music.duck(false); return false; }
    return canSpeak();
  });

  /* Space plays and pauses, arrows step. A video player people already
     know how to use is one less thing to explain. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest('input, textarea, button')) return;
      if (e.code === 'Space') { e.preventDefault(); setPlaying((p) => !p); }
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const done = scenes.slice(0, si).reduce((n, s) => n + s.beats.length, 0) + beat + 1;
  const Art = scene.Art;

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>{title}</strong>
            <span className="lab-sub">{subtitle}</span>
          </div>
        </div>
        <Link to={backTo} className="btn btn-sm btn-ghost">{backLabel}</Link>
      </header>

      <div className="bl xp">
        {/* ---------- the stage ---------- */}
        <div className="bl-stage xp-stage">
          <svg viewBox={viewBox} className="bl-canvas" aria-hidden="true">
            {Defs ? <Defs /> : null}
            <g className="bl-grid">
              {Array.from({ length: 17 }, (_, i) => (
                <line key={`v${i}`} x1={i * 10} y1="0" x2={i * 10} y2="90" />
              ))}
              {Array.from({ length: 10 }, (_, i) => (
                <line key={`h${i}`} x1="0" y1={i * 10} x2="160" y2={i * 10} />
              ))}
            </g>
            {/* keyed on the scene so every entrance animation replays */}
            <g key={scene.id}><Art beat={beat} /></g>
          </svg>

          {subs && (
            <div className="bl-caption">
              <span className="bl-caption-scene">
                Scene {si + 1} of {scenes.length} · {scene.title}
              </span>
              <p key={`${scene.id}-${beat}`}>{line.say}</p>
            </div>
          )}
        </div>

        {/* ---------- controls ---------- */}
        <div className="bl-bar">
          <span className="bl-progress"><span style={{ width: `${(done / totalBeats) * 100}%` }} /></span>
        </div>

        <div className="launch-controls bl-controls">
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              if (finished) { goScene(0); setPlaying(true); return; }
              setPlaying((p) => !p);
            }}
          >
            {finished ? '↻ Watch again' : playing ? '❚❚ Pause' : '▶ Play'}
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => step(-1)} disabled={si === 0 && beat === 0}>
            ← Back
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => step(1)} disabled={atEnd}>
            Next →
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-ghost ${narrating ? 'is-on' : ''}`}
            onClick={toggleNarration}
            aria-pressed={narrating}
          >
            {narrating ? '🎙 Voiceover on' : '🎙 Voiceover off'}
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-ghost ${sound ? 'is-on' : ''}`}
            onClick={toggleSound}
            aria-pressed={sound}
          >
            {sound ? '🔊 Music & effects on' : '🔇 Music & effects off'}
          </button>
          <button
            type="button"
            className={`btn btn-sm btn-ghost ${subs ? 'is-on' : ''}`}
            onClick={() => setSubs((s) => !s)}
            aria-pressed={subs}
          >
            {subs ? '💬 Subtitles on' : '💬 Subtitles off'}
          </button>
        </div>

        {/* ---------- jump to any scene ---------- */}
        <ol className="bl-scenes">
          {scenes.map((s, n) => (
            <li key={s.id}>
              <button
                type="button"
                className={`bl-scene ${n === si ? 'is-now' : ''} ${n < si ? 'is-done' : ''}`}
                onClick={() => { setPlaying(false); goScene(n); }}
                aria-current={n === si ? 'step' : undefined}
              >
                <span className="bl-scene-n">{n + 1}</span>
                {s.title}
              </button>
            </li>
          ))}
        </ol>

        {watched && closing && (
          <div className="lab-done">
            <div>
              <strong>{closing.title}</strong>
              <p style={{ margin: '0.3rem 0 0' }}>{closing.text}</p>
            </div>
            <Link to={backTo} className="btn btn-sm">{backLabel}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
